import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Waves, Eye, EyeSlash, ArrowLeft, Gift, Sun, Moon, Lightning } from '@phosphor-icons/react';
import { Logo, LogoText } from '../components/Logo';

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { register } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    const result = await register(email, password, name);
    if (result.success) { navigate('/dashboard'); } else { setError(result.error); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg-main)', color: 'var(--text-primary)' }}>
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 border-r p-12 flex-col justify-between relative overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)' }}>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl" style={{ background: 'var(--accent-bg)' }} />

        <div className="relative">
          <Link to="/" className="flex items-center gap-3" data-testid="register-logo">
            <Logo size="lg" />
            <span className="font-black text-2xl tracking-tight">index24hrs<span style={{ color: 'var(--accent)' }}>.com</span></span>
          </Link>
        </div>

        <div className="space-y-6 relative">
          <h2 className="text-4xl font-black tracking-tight leading-tight">
            Start Indexing<br />
            <span style={{ color: 'var(--accent)' }}>In Seconds</span>
          </h2>
          <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
            Create your account and start indexing URLs immediately. Purchase credits to begin.
          </p>

          <div className="border rounded-xl p-6" style={{ background: 'var(--accent-bg)', borderColor: 'var(--border-accent)' }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--accent)' }}>
                <Gift weight="duotone" className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-lg">Instant Indexing</span>
            </div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Create your account and purchase credits to start indexing URLs in seconds!
            </p>
          </div>
        </div>

        <p className="text-sm relative" style={{ color: 'var(--text-dimmed)' }}>© 2026 index24hrs.com</p>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 relative">
        <button onClick={toggleTheme} className="absolute top-4 right-4 p-2 rounded-lg transition-colors" style={{ color: 'var(--text-secondary)' }} data-testid="register-theme-toggle">
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>

        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-3" data-testid="register-mobile-logo">
              <Logo size="md" />
              <LogoText size="md" />
            </Link>
          </div>

          <div>
            <Link to="/" className="inline-flex items-center gap-2 text-sm transition-colors mb-6" style={{ color: 'var(--text-secondary)' }} data-testid="back-to-home-register">
              <ArrowLeft className="w-4 h-4" /> Back to home
            </Link>
            <h1 className="text-3xl font-black tracking-tight">Create account</h1>
            <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>Sign up to start indexing your URLs</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 text-sm rounded-lg" data-testid="register-error">{error}</div>
            )}

            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Full Name</label>
              <input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} className="input-field" placeholder="John Doe" required data-testid="register-name-input" />
            </div>

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Email</label>
              <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-field" placeholder="you@example.com" required data-testid="register-email-input" />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Password</label>
              <div className="relative">
                <input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="input-field pr-12" placeholder="••••••••" required minLength={6} data-testid="register-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors" style={{ color: 'var(--text-secondary)' }} data-testid="toggle-password-register">
                  {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Minimum 6 characters</p>
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="register-submit">
              {loading ? <div className="w-5 h-5 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--bg-main)', opacity: 0.3, borderTopColor: 'var(--bg-main)' }} /> : <><Lightning weight="bold" className="w-5 h-5" /> Create Account</>}
            </button>
          </form>

          <p className="text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
            Already have an account?{' '}
            <Link to="/login" className="font-medium" style={{ color: 'var(--accent)' }} data-testid="go-to-login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
